import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListCard,
  Row,
  SectionHeader,
  SegmentedControl,
} from '@gonka-wallet/ui-tokens';
import {
  WalletError,
  formatExplorerUrl,
  type HistoryItem,
} from '@gonka-wallet/chain';
import { shortenAddress } from '@gonka-wallet/crypto';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { useActiveEntry } from '../stores/walletStore.js';
import { useHistory } from '../hooks/useHistory.js';
import { useToastStore } from '../stores/toastStore.js';
import { formatGnkDisplay } from '../lib/format.js';

type FilterValue = 'all' | 'sent' | 'received';

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function groupTxsByDay(
  txs: readonly HistoryItem[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Array<[string, HistoryItem[]]> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);

  const groups = new Map<string, HistoryItem[]>();
  for (const tx of txs) {
    const d = new Date(tx.timestamp);
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let key: string;
    if (dayStart.getTime() === today.getTime()) {
      key =
        t('history.today') +
        ' · ' +
        dayStart.toLocaleDateString('en', { day: '2-digit', month: 'short' });
    } else if (dayStart.getTime() === yesterday.getTime()) {
      key =
        t('history.yesterday') +
        ' · ' +
        dayStart.toLocaleDateString('en', { day: '2-digit', month: 'short' });
    } else {
      key = dayStart.toLocaleDateString('en', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }
    const existing = groups.get(key) ?? [];
    existing.push(tx);
    groups.set(key, existing);
  }
  return Array.from(groups.entries());
}

export function History() {
  const { t } = useTranslation();
  const active = useActiveEntry();
  const address =
    active?.kind === 'full'
      ? active.wallet.addresses[0]
      : active?.kind === 'watch'
        ? active.watched.address
        : null;
  const historyQuery = useHistory(address ?? null);
  const { data, isLoading } = historyQuery;
  const pushToast = useToastStore((s) => s.push);
  const [selected, setSelected] = useState<HistoryItem | null>(null);

  useEffect(() => {
    if (!historyQuery.error) return;
    const msg =
      historyQuery.error instanceof WalletError
        ? t(historyQuery.error.i18nKey)
        : t('errors.network.allDown');
    pushToast(msg, 'error');
  }, [historyQuery.error, t, pushToast]);

  const [hashCopied, setHashCopied] = useState(false);
  async function copyHash(hash: string) {
    try {
      await navigator.clipboard.writeText(hash);
      setHashCopied(true);
      setTimeout(() => setHashCopied(false), 1500);
    } catch {
      pushToast(t('common.copyFailed'), 'error');
    }
  }

  const [filter, setFilter] = useState<FilterValue>('all');
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const filteredItems =
    data?.items.filter((tx) => filter === 'all' || tx.direction === filter) ??
    [];
  const visibleItems = filteredItems.slice(0, visibleCount);
  const hiddenCount = Math.max(0, filteredItems.length - visibleCount);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter, address]);

  return (
    <div className="flex min-h-screen flex-col gap-4 p-4">
      <h1 className="text-2xl font-semibold">{t('history.title')}</h1>

      <SegmentedControl<FilterValue>
        options={['all', 'sent', 'received'] as const}
        value={filter}
        onChange={setFilter}
        renderLabel={(opt) => {
          const labels: Record<FilterValue, string> = {
            all: t('history.filter.all'),
            sent: t('history.filter.sent'),
            received: t('history.filter.received'),
          };
          return labels[opt];
        }}
        ariaLabel="History filter"
        className="w-full"
      />

      {isLoading && (
        <p className="text-center text-sm text-text-secondary">Loading…</p>
      )}

      {filteredItems.length === 0 && !isLoading && data ? (
        <div className="py-12 text-center text-sm text-text-tertiary">
          {t('history.empty')}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {groupTxsByDay(visibleItems, t).map(([day, dayTxs]) => (
            <div key={day} className="flex flex-col gap-2">
              <SectionHeader>{day}</SectionHeader>
              <ListCard>
                {dayTxs.map((tx) => {
                  const isIncoming = tx.direction === 'received';
                  return (
                    <Row
                      key={tx.hash}
                      label={
                        isIncoming
                          ? t('history.received')
                          : t('history.sent')
                      }
                      meta={
                        shortenAddress(tx.counterparty) +
                        ' · ' +
                        formatTime(tx.timestamp)
                      }
                      value={`${isIncoming ? '+' : '−'}${formatGnkDisplay(tx.amount)} GNK`}
                      valueTone={isIncoming ? 'pos' : 'neg'}
                      onClick={() => setSelected(tx)}
                    />
                  );
                })}
              </ListCard>
            </div>
          ))}
          {hiddenCount > 0 && (
            <div className="flex justify-center pt-1">
              <button
                type="button"
                onClick={() =>
                  setVisibleCount((n) => Math.min(n + PAGE_SIZE, filteredItems.length))
                }
                className="inline-flex items-center gap-2 rounded-full border border-border-hairline bg-surface-1 px-4 py-1.5 text-[11px] font-medium text-text-primary transition-colors hover:bg-surface-2"
              >
                {t('history.loadMore')}
                <span className="font-mono tabular-nums text-text-tertiary">
                  +{hiddenCount}
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {data && (
        <p className="mt-auto pt-2 text-center text-[10px] text-text-secondary">
          {t(`history.source.${data.source}`)}
        </p>
      )}

      {selected && (
        <TxDetailSheet
          tx={selected}
          copied={hashCopied}
          onClose={() => {
            setSelected(null);
            setHashCopied(false);
          }}
          onCopyHash={() => copyHash(selected.hash)}
        />
      )}
    </div>
  );
}

interface TxDetailSheetProps {
  readonly tx: HistoryItem;
  readonly copied: boolean;
  readonly onClose: () => void;
  readonly onCopyHash: () => void;
}

function TxDetailSheet({ tx, copied, onClose, onCopyHash }: TxDetailSheetProps) {
  const { t } = useTranslation();
  const dragControls = useDragControls();
  const explorerUrl = formatExplorerUrl(tx.hash);
  const directionLabel =
    tx.direction === 'sent' ? t('history.sent') : t('history.received');
  const counterpartyLabel =
    tx.direction === 'sent'
      ? t('history.detail.toAddress')
      : t('history.detail.fromAddress');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/40"
        role="dialog"
        aria-modal="true"
      />
      <motion.div
        key="sheet"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        drag="y"
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        dragMomentum={false}
        onDragEnd={(_, { offset, velocity }) => {
          if (offset.y > 120 || velocity.y > 500) onClose();
        }}
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[88vh] flex-col rounded-t-[20px] bg-surface-1 shadow-[0_-10px_40px_rgba(0,0,0,0.2)]"
      >
        <div
          onPointerDown={(event) => dragControls.start(event)}
          className="flex shrink-0 cursor-grab touch-none justify-center px-5 pb-2 pt-3 active:cursor-grabbing"
        >
          <div className="h-1 w-9 rounded-full bg-border-strong" aria-hidden />
        </div>
        <div className="overflow-auto px-5 pb-5">

        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[18px] font-semibold text-text-primary">
            {directionLabel}
          </h2>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="flex h-8 w-8 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-2"
          >
            <span aria-hidden className="text-[18px]">×</span>
          </button>
        </div>

        <div
          className={`mb-4 font-mono text-[30px] font-medium tabular-nums tracking-[-1px] ${
            tx.direction === 'sent' ? 'text-neg' : 'text-pos'
          }`}
        >
          {tx.direction === 'sent' ? '−' : '+'}
          {formatGnkDisplay(tx.amount)}
          <span className="ml-1.5 font-sans text-[13px] text-text-tertiary">GNK</span>
        </div>

        <DetailRow label={counterpartyLabel}>
          <span className="break-all font-mono text-[12px]">{tx.counterparty}</span>
        </DetailRow>
        <DetailRow label={t('history.detail.timestamp')}>
          {formatTimestamp(tx.timestamp)}
        </DetailRow>
        <DetailRow label={t('history.detail.height')}>{tx.height}</DetailRow>
        <DetailRow label={t('history.detail.hash')}>
          <span className="break-all font-mono text-[12px]">{tx.hash}</span>
        </DetailRow>
        {tx.memo && (
          <DetailRow label={t('history.detail.memo')}>{tx.memo}</DetailRow>
        )}

        <div className="mt-5 flex items-center justify-between border-t border-border-hairline pt-4 text-[11px]">
          <button
            type="button"
            onClick={onCopyHash}
            className="font-medium text-text-primary hover:opacity-80"
          >
            {copied ? `✓ ${t('common.copied')}` : t('history.detail.copyHash')}
          </button>
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-link hover:opacity-80"
            >
              {t('history.detail.viewExplorer')} ↗
            </a>
          )}
        </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function DetailRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="mb-3 border-b border-border-hairline pb-2.5">
      <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 text-[13px] text-text-primary">{children}</div>
    </div>
  );
}
